import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type { SessionDescriptor } from '@sciolingo/api-client'
import { usePlan } from '../../api/queries'
import { paths } from '../../app/routes'
import { ArrowRightIcon, BookIcon, BookmarkIcon, ChartIcon, TargetIcon } from '../../shared/components/icons'
import { useOnline } from '../../shared/hooks'
import { ApkDownloadLink } from '../install/ApkDownloadLink'
import { InstallNudge } from '../install/InstallNudge'
import styles from './HomeScreen.module.css'

/**
 * The shell's entry point, built to the Homepage artboard on the design canvas
 * (§2.1).
 *
 * It **displays** plan state and never computes it — the adaptive plan stays
 * server-side, and the server is authoritative for it (§2 product decision 1).
 * So the recommended-start card's copy and its target both arrive from
 * `GET /plan`; the client picks nothing.
 * The feature list below is static product copy and carries no plan meaning.
 *
 * Where the mock-up and the specification disagree on behaviour, §2.1 says the
 * specification governs. One case applies here: the mock shows the card
 * unconditionally, and this renders it only when the server sends one.
 */

interface Feature {
  icon: ReactNode
  title: string
  detail: string
}

const FEATURES: Feature[] = [
  {
    icon: <BookIcon />,
    title: 'Teorie a procvičení',
    detail: 'Výklad k typu úlohy a úlohy hned k němu.',
  },
  {
    icon: <TargetIcon />,
    title: 'Cílený trénink',
    detail: 'Vlastní téma a počet úloh.',
  },
  {
    icon: <BookmarkIcon />,
    title: 'Test nanečisto',
    detail: 'Celý test jako naostro.',
  },
  {
    icon: <ChartIcon />,
    title: 'Výsledky a slabiny',
    detail: 'Kde ti to jde a kde tě to brzdí.',
  },
]

export function HomeScreen() {
  const online = useOnline()
  const [showAll, setShowAll] = useState(false)

  const plan = usePlan()

  const recommendation = plan.data?.recommendation ?? null
  const recommended = recommendation
    ? plan.data?.descriptors.find((d) => d.id === recommendation.descriptorId)
    : undefined

  return (
    <div className={styles.home}>
      <h1 className={styles.title}>Vítej ve Sciolingu</h1>
      <p className={styles.lede}>Příprava na OSP na skutečných úlohách.</p>
      <p>
        <ApkDownloadLink />
      </p>

      <hr className={styles.rule} />

      <h2 className={styles.eyebrow}>Co v appce najdeš</h2>
      <ul className={styles.features}>
        {FEATURES.map((feature) => (
          <li className={styles.feature} key={feature.title}>
            <span className={styles.featureIcon}>{feature.icon}</span>
            <span className={styles.featureText}>
              <span className={styles.featureTitle}>{feature.title}</span>
              <span className={styles.featureDetail}>{feature.detail}</span>
            </span>
          </li>
        ))}
      </ul>

      <InstallNudge />

      {plan.isPending ? <p className="muted">Načítám plán…</p> : null}

      {plan.isError ? (
        <div className="card card--warn">
          <p>
            {online
              ? 'Plán se nepodařilo načíst.'
              : 'Jsi offline. Plán se načte, jakmile bude připojení k dispozici.'}
          </p>
          <button className="button" type="button" onClick={() => void plan.refetch()}>
            Zkusit znovu
          </button>
        </div>
      ) : null}

      {recommendation && recommended ? (
        <section className={styles.recommend} aria-labelledby="recommend-title">
          <p className={styles.recommendKicker}>Doporučený začátek</p>
          <h3 className={styles.recommendTitle} id="recommend-title">
            {recommendation.title}
          </h3>
          <p className={styles.recommendBody}>{recommendation.body}</p>
          <p className={styles.recommendMeta}>
            <span className={styles.chip}>{describe(recommended)}</span>
          </p>
        </section>
      ) : null}

      {plan.data ? (
        <div className={styles.actions}>
          {recommended ? (
            <Link className={`button button--primary ${styles.buttonBlock}`} to={paths.drill(recommended.id)}>
              Sestavit můj plán
            </Link>
          ) : null}

          {/*
            The mock's "Zatím bez plánu →" leads to the un-planned path. Rather
            than a second screen, it reveals the session list that is already
            the honest content of this one.
          */}
          {recommended && !showAll ? (
            <button className={styles.linklike} type="button" onClick={() => setShowAll(true)}>
              Zatím bez plánu <ArrowRightIcon className={styles.linklikeArrow} />
            </button>
          ) : null}
        </div>
      ) : null}

      {plan.data && (showAll || !recommended) ? (
        <section className="stack" aria-labelledby="own-choice">
          <h2 className={styles.eyebrow} id="own-choice">
            {plan.data.headline}
          </h2>
          <ul className={styles.cards}>
            {/* The recommended session already has its own card above. */}
            {plan.data.descriptors
              .filter((d) => d.id !== recommendation?.descriptorId)
              .map((descriptor) => (
                <li key={descriptor.id}>
                  <Link className={`card ${styles.cardAction}`} to={paths.drill(descriptor.id)}>
                    <span className={styles.cardKicker}>{descriptor.kind === 'test' ? 'Test' : 'Procvičování'}</span>
                    <span className="card__title">{descriptor.title}</span>
                    <span className="muted">{descriptor.subtitle}</span>
                  </Link>
                </li>
              ))}
          </ul>
        </section>
      ) : null}
    </div>
  )
}

/** The chip under the recommendation: "Diagnostika ~25 min". */
function describe(descriptor: SessionDescriptor): string {
  if (descriptor.durationSec === null) return `${descriptor.title} · ${descriptor.itemCount} úloh`
  return `${descriptor.title} ~${Math.round(descriptor.durationSec / 60)} min`
}
